import { proxy } from 'valtio'

/** One entry per error screen in the mockups. */
export type ErrorScreenKind =
  | 'replay-open'
  | 'network'
  | 'version-unsupported'
  | 'playback'
  | 'render'
  | 'not-found'
  | 'unknown'

/** Only the screens whose mockup shows a support badge get a code. */
const CODE_PREFIX: Partial<Record<ErrorScreenKind, string>> = {
  playback: 'PLB',
  unknown: 'UN'
}

/** `PLB-260522-A3F9K2` — prefix, YYMMDD, then 6 random chars, as in the mockup. */
const buildErrorCode = (kind: ErrorScreenKind) => {
  const prefix = CODE_PREFIX[kind]
  if (!prefix) return ''
  const now = new Date()
  const date = [now.getFullYear() % 100, now.getMonth() + 1, now.getDate()]
    .map(part => String(part).padStart(2, '0')).join('')
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ0123456789' // no I/O — unreadable when dictated
  const suffix = Array.from(
    { length: 6 },
    () => alphabet[Math.floor(Math.random() * alphabet.length)]
  ).join('')
  return `${prefix}-${date}-${suffix}`
}

export const errorScreenState = proxy({
  kind: null as ErrorScreenKind | null,
  /** Technical reason behind the failure — never shown, kept for the console. */
  reason: '',
  /** Support code for the badge; empty for screens whose mockup has none. */
  code: '',
  /** Substituted into the description, e.g. the version numbers. */
  params: {} as Record<string, string>
})

export const showErrorScreen = (
  kind: ErrorScreenKind,
  reason = '',
  params: Record<string, string> = {}
) => {
  // The screen itself stays user-facing and vague, so the real cause has to
  // land somewhere reachable when someone reports the error.
  console.error(`[error-screen:${kind}]`, reason)
  // A failure usually cascades into more failures; the first one is the one
  // that explains what happened, so later ones must not overwrite it.
  if (errorScreenState.kind) return
  errorScreenState.kind = kind
  errorScreenState.reason = reason
  errorScreenState.params = params
  errorScreenState.code = buildErrorCode(kind)
}

export const hideErrorScreen = () => {
  errorScreenState.kind = null
  errorScreenState.reason = ''
  errorScreenState.params = {}
  errorScreenState.code = ''
}
